<template>
    <div class="oauth-callback">
        <div class="oauth-callback__card">
            <div class="oauth-callback__loading" v-if="loading">
                <img
                    :src="require('@/assets/images/apple/sign-in-with-apple-logo-only.svg')"
                    alt="Apple"
                    class="oauth-callback__logo"
                />
                <div class="oauth-callback__dots">
                    <span></span>
                    <span></span>
                    <span></span>
                </div>
                <p class="oauth-callback__text">{{ $t('Completing sign in...') }}</p>
            </div>
            <div class="oauth-callback__error" v-else-if="error">
                <div class="oauth-callback__error-icon">
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                        <path d="M12 7v6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        <circle cx="12" cy="16" r="1" fill="currentColor"/>
                    </svg>
                </div>
                <h2 class="oauth-callback__error-title">{{ $t('Sign in failed') }}</h2>
                <p class="oauth-callback__error-text">{{ error }}</p>
                <ui-button :to="loginRoute">
                    {{ $t('Back to Login') }}
                </ui-button>
            </div>
        </div>
    </div>
</template>

<script>
import socialLoginMixin from '@/plugins/mixin/social-login.mixin';
import UiButton from '@/components/ui/buttons/Button.vue';

function readAppleCallbackPayload() {
    const fromStorage = sessionStorage.getItem('apple_oauth_callback');
    if (fromStorage) {
        sessionStorage.removeItem('apple_oauth_callback');
        try {
            return JSON.parse(fromStorage);
        } catch {
            return null;
        }
    }
    const hash = globalThis.location.hash.replace(/^#/, '');
    if (!hash) {
        return null;
    }
    return Object.fromEntries(new URLSearchParams(hash));
}

export default {
    name: 'AppleCallbackPage',
    components: { UiButton },
    mixins: [socialLoginMixin],
    data() {
        return {
            loading: true,
            error: null,
            user: {
                _tk: null,
                name: 'apple',
                siteId: null,
                redirectUrl: null,
            }
        };
    },
    computed: {
        loginRoute() {
            const query = { ...this.$route.query };
            ['id_token', 'code', 'state', 'user', 'error', 'nonce'].forEach((param) => {
                delete query[param];
            });
            if (!query.apple_login_mode) {
                query.apple_login_mode = 'redirection';
            }
            if (!query.google_login_mode) {
                query.google_login_mode = 'redirection';
            }
            return { path: '/login', query };
        }
    },
    async mounted() {
        await this.handleCallback();
    },
    methods: {
        async handleCallback() {
            try {
                const params = readAppleCallbackPayload() || {};

                if (params.error) {
                    this.error = this.$t('Apple sign in was cancelled or failed');
                    this.loading = false;
                    return;
                }

                const id_token = params.id_token;
                if (!id_token) {
                    this.error = this.$t('Invalid callback response');
                    this.loading = false;
                    return;
                }

                const state = params.state;
                const savedState = sessionStorage.getItem('apple_oauth_state');
                if (!savedState || !state || savedState !== state) {
                    this.error = this.$t('Invalid state parameter');
                    this.loading = false;
                    return;
                }

                const stateData = JSON.parse(atob(state));
                sessionStorage.removeItem('apple_oauth_state');

                this.user.siteId = stateData.site_id;
                this.user.redirectUrl = stateData.redirect_url;
                this.user._tk = id_token;

                // Put site/redirect on the URL so /api/login can resolve them from Referer
                // (same pattern as google_callback_page).
                const restoredQuery = { ...this.$route.query };
                if (stateData.site_id) {
                    restoredQuery.site_id = stateData.site_id;
                }
                if (stateData.redirect_url) {
                    restoredQuery.redirect_url = stateData.redirect_url;
                }
                restoredQuery.apple_login_mode = 'redirection';
                restoredQuery.google_login_mode = 'redirection';
                await this.$router.replace({ query: restoredQuery });

                if (params.user) {
                    try {
                        const appleUser = typeof params.user === 'string'
                            ? JSON.parse(params.user)
                            : params.user;
                        if (appleUser?.name?.firstName) {
                            this.user.first_name = appleUser.name.firstName;
                        }
                        if (appleUser?.name?.lastName) {
                            this.user.last_name = appleUser.name.lastName;
                        }
                    } catch {
                        // First-auth name payload is optional
                    }
                }

                if (globalThis.history?.replaceState && globalThis.location.hash) {
                    globalThis.history.replaceState(
                        null,
                        '',
                        `${globalThis.location.pathname}${globalThis.location.search}`
                    );
                }

                await this.socialNetWorkSucess(this.user);
            } catch (err) {
                if (err.appleLinkRequired) {
                    this.error = this.$t(
                        'This Apple account needs to be linked. Please sign in again and complete account linking.'
                    );
                    this.loading = false;
                    return;
                }
                this.error = err.response?.data?.message || this.$t('An error occurred during sign in');
                this.loading = false;
            }
        },
        showError(error) {
            this.error = error.response?.data?.message || this.$t('An error occurred during sign in');
            this.loading = false;
        }
    }
};
</script>

<style lang="scss" scoped>
.oauth-callback {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;

    &__card {
        background-color: #ffffff;
        border-radius: 12px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        padding: 48px 40px;
        text-align: center;
        max-width: 400px;
        width: 100%;
    }

    &__loading {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px;
    }

    &__logo {
        width: 48px;
        height: 48px;
    }

    &__dots {
        display: flex;
        gap: 6px;

        span {
            width: 8px;
            height: 8px;
            background-color: #dadce0;
            border-radius: 50%;
            animation: bounce 1.4s ease-in-out infinite;

            &:nth-child(1) {
                animation-delay: 0s;
            }

            &:nth-child(2) {
                animation-delay: 0.16s;
            }

            &:nth-child(3) {
                animation-delay: 0.32s;
            }
        }
    }

    &__text {
        color: #5f6368;
        font-size: 15px;
        font-weight: 400;
        margin: 0;
    }

    &__error {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
    }

    &__error-icon {
        width: 56px;
        height: 56px;
        color: #d93025;
    }

    &__error-title {
        color: #202124;
        font-size: 20px;
        font-weight: 600;
        margin: 0;
    }

    &__error-text {
        color: #5f6368;
        font-size: 14px;
        margin: 0;
        line-height: 1.5;
    }
}

@keyframes bounce {
    0%, 80%, 100% {
        transform: scale(0.6);
        background-color: #dadce0;
    }
    40% {
        transform: scale(1);
        background-color: var(--color-accent-base, #000000);
    }
}
</style>
